import { RecordsDiff, TLRecord, TLShapeId } from "tldraw";
import { BaseAgentManager } from "./BaseAgentManager";
import { isAuthoredShape } from "../../utils/authoredContent";

/**
 * Tracks shapes created by the agent during a prompt chain so they can be unlocked when it ends.
 * This is cleared when starting a new top-level prompt (not nested prompts).
 */
export class AgentCreatedShapesManager extends BaseAgentManager {
  /**
   * Set of shape IDs created during the current prompt chain.
   * Persists across nested prompts but clears when a new top-level prompt starts.
   */
  private createdShapeIds: Set<TLShapeId> = new Set();

  /**
   * Reset the manager to its initial state.
   */
  reset(): void {
    this.createdShapeIds.clear();
  }

  /**
   * Clear tracking of created shapes (called at start of new prompt chain).
   */
  clear(): void {
    this.createdShapeIds.clear();
  }

  /**
   * Track shapes created from a diff.
   * Should be called after each action is applied.
   */
  trackShapesFromDiff(diff: RecordsDiff<TLRecord>): void {
    for (const [id, record] of Object.entries(diff.added)) {
      if (record.typeName === "shape") {
        this.createdShapeIds.add(id as TLShapeId);
      }
    }

    // Track removed shapes and remove from created set
    for (const id of Object.keys(diff.removed)) {
      this.createdShapeIds.delete(id as TLShapeId);
    }
  }

  /**
   * Get all shape IDs created during the current prompt chain.
   */
  private getIds(): TLShapeId[] {
    return Array.from(this.createdShapeIds);
  }

  /**
   * Get shapes created during the prompt chain that still exist.
   */
  private getShapes() {
    const { editor } = this.agent;
    return this.getIds()
      .map((id) => editor.getShape(id))
      .filter((shape) => shape !== undefined);
  }

  /**
   * Unlock all shapes created during the current prompt chain.
   * Called when the prompt completes to make shapes editable again.
   *
   * Authored content is exempt. This method exists to clear the STREAMING
   * lock (a half-drawn shape is locked so the student can't grab it mid-
   * stroke); authored content is locked by PROVENANCE — it is the problem
   * statement, and the student must not move it at all. Unlocking it here is
   * what made the practice question draggable (ENGG-2438).
   */
  unlock(): void {
    const { editor } = this.agent;
    const createdShapes = this.getShapes();
    const lockedShapes = createdShapes.filter(
      (shape) => shape.isLocked && !isAuthoredShape(shape),
    );
    if (lockedShapes.length === 0) return;

    editor.run(
      () => {
        for (const shape of lockedShapes) {
          editor.updateShape({
            id: shape.id,
            type: shape.type,
            isLocked: false,
          });
        }
      },
      { ignoreShapeLock: true, history: "ignore" },
    );
  }
}
